import { useTreeViewState } from '@aibees/elephant/context/tree-view';
import { classnames } from '@aibees/shared/utils';
import { Box } from '@mui/material';
import {
  RichTreeView,
  TreeItem2Props,
  TreeViewBaseItem,
  useTreeViewApiRef,
} from '@mui/x-tree-view';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { CustomTreeItem } from './custom-tree-item';
import * as SX from './sx';
import type { CheckboxTreeViewProps as Props, TreeItemData } from './types';
import { useHandleSelection } from './use-handle-selection';
import { getAllTreeItemIdsIterative } from './utils';
import { useSearch } from '@aibees/shared/context';

const EMPTY_SELECTION: string[] = [];

export const CheckboxTreeView = memo(
  ({
    className,
    items,
    filteredItems,
    onChange,
    search,
    multiSelect,
    checkboxPosition,
    metadataApiLoading,
    metadataEnabled,
    iconSX,
    hideLogoFallback,
    avartarFallbackIcon,
    isSearchQueryReachedMinimumLengthThreshold,
  }: Props) => {
    const apiRef = useTreeViewApiRef();
    const apiRefForAllItems = useTreeViewApiRef();
    const selectedValues = useTreeViewState();
    const [expandedItemIds, setExpandedItemIds] = useState<string[]>();
    const { setSearch: setHighlightedSearch } = useSearch();

    const { handleSelection } = useHandleSelection(
      () => apiRefForAllItems.current,
      onChange
    );

    useEffect(() => {
      setHighlightedSearch(search);
    }, [search, setHighlightedSearch]);

    const controlledExpandedItems = useMemo(() => {
      return isSearchQueryReachedMinimumLengthThreshold;
    }, [isSearchQueryReachedMinimumLengthThreshold]);

    const expandedIdsFromFiltered = useMemo(
      () => getAllTreeItemIdsIterative(filteredItems),
      [filteredItems]
    );

    useEffect(() => {
      if (controlledExpandedItems) {
        setExpandedItemIds((prev) => {
          const prevSet = new Set(prev ?? []);
          const hasNewIds = expandedIdsFromFiltered.some(
            (id) => !prevSet.has(id)
          );
          if (prev && !hasNewIds) return prev;
          return [...(prev ?? []), ...expandedIdsFromFiltered];
        });
      } else {
        setExpandedItemIds(undefined);
      }
    }, [expandedIdsFromFiltered, controlledExpandedItems]);

    const toggleExpantion = useCallback(
      (_event: React.SyntheticEvent, itemId: string, isExpanded: boolean) => {
        if (!isExpanded) {
          setExpandedItemIds((prev) => prev?.filter((i) => i !== itemId));
        } else {
          setExpandedItemIds((prev) => [...(prev ?? []), itemId]);
        }
      },
      []
    );

    const renderCustomTreeItem = useCallback(
      (props: TreeItem2Props) => (
        <CustomTreeItem
          {...props}
          onClickHandler={handleSelection}
          checkboxPosition={checkboxPosition}
          multiSelect={multiSelect}
          metadataEnabled={metadataEnabled}
          metadataApiLoading={metadataApiLoading}
          iconSx={iconSX}
          avartarFallbackIcon={avartarFallbackIcon}
          hideLogoFallback={hideLogoFallback}
        />
      ),
      [
        handleSelection,
        checkboxPosition,
        multiSelect,
        metadataEnabled,
        metadataApiLoading,
        iconSX,
        avartarFallbackIcon,
        hideLogoFallback,
      ]
    );

    const slots = useMemo(
      () => ({ item: renderCustomTreeItem }),
      [renderCustomTreeItem]
    );

    return (
      <Box
        paddingY={1}
        className={classnames('CheckboxTreeView', className)}
        sx={SX.root}
      >
        <RichTreeView<TreeViewBaseItem<TreeItemData>, true>
          apiRef={apiRef}
          items={filteredItems}
          checkboxSelection={multiSelect}
          multiSelect={multiSelect || undefined}
          selectedItems={selectedValues}
          slots={slots}
          expandedItems={expandedItemIds}
          onItemExpansionToggle={
            controlledExpandedItems ? toggleExpantion : undefined
          }
        />
        {/* this hidden treeview is just for using its API REF for all tree items so the 
            selection/deselection logic would be implemnted on all tree items not just filtered ones */}
        <div style={{ display: 'none' }}>
          <RichTreeView<TreeViewBaseItem<TreeItemData>, true>
            apiRef={apiRefForAllItems}
            items={items}
            checkboxSelection={multiSelect}
            multiSelect={multiSelect || undefined}
            selectedItems={EMPTY_SELECTION}
          />
        </div>
      </Box>
    );
  }
);

CheckboxTreeView.displayName = 'CheckboxTreeView';
